// Interface for Property Address
export interface ISubscriptionPropertyAddress {
  // buildingNumber: string;
  // subStreet?: string;
  // street: string;
  // barangay: string;
  // city: string;
  // zipCode: string;
  details?: string;
  province: string;
  country: string;
}

// Interface for Property Details
export interface ISubscriptionPropertyDetails {
  propertyName: string;
  address: ISubscriptionPropertyAddress;
  // logo: File[]; // Assuming logo is a file upload
  emailAddress: string;
  website?: string;
  tin?: string;
  phoneNumber?: string;
}

// Interface for Authorized Person (Admin)
export interface ISubscriptionAuthorizedPerson {
  firstName: string;
  lastName: string;
  middleName?: string;
  email: string;
  phoneNumber: string;
}

// Interface for Contact Person Billing
export interface ISubscriptionContactPersonBilling {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  email?: string;
  phoneNumber?: string;
}

// Interface for Subscription
export interface ISubscriptionLicense {
  numberOfRooms: number;
  numberOfUsers: number;
  concurrentUsers: number;
  validity: number;
}

export type ISubscriptionProduct =
  | 'Xenia Cloud Front Office System'
  | 'iServe Cloud Point of Sale'
  | 'Online Booking Channel'
  | 'Hermes Cloud Bookkeeping'
  | 'Hermes Cloud Bookkeeping (CAS)'
  | 'IXORP Booking Engine'
  | 'Iris Channel Manager'
  | 'Website Development and Hosting';

export type ISubscriptionProductCode =
  | 'FO'
  | 'POS'
  | 'OBC'
  | 'ABK'
  | 'ABKCAS'
  | 'IRIS'
  | 'IXORP'
  | 'WEB';
export type ISubscriptionPlan = 'Monthly' | 'Quarterly' | 'Annually';

export type InitialSelectedKey =
  | 'xeniaCloudFOS'
  | 'iServeCloud'
  | 'onlineBookingChannel'
  | 'hermesCloudBookkeeping'
  | 'hermesCloudBookkeepingCAS'
  | 'iris'
  | 'ixorp'
  | 'website';

export const PRODUCT_KEY_MAP: Record<InitialSelectedKey, ISubscriptionProduct> =
  {
    xeniaCloudFOS: 'Xenia Cloud Front Office System',
    iServeCloud: 'iServe Cloud Point of Sale',
    onlineBookingChannel: 'Online Booking Channel',
    hermesCloudBookkeeping: 'Hermes Cloud Bookkeeping',
    hermesCloudBookkeepingCAS: 'Hermes Cloud Bookkeeping (CAS)',
    iris: 'Iris Channel Manager',
    ixorp: 'IXORP Booking Engine',
    website: 'Website Development and Hosting',
  };

export const PRODUCT_CODE_MAP: Record<
  InitialSelectedKey,
  ISubscriptionProductCode
> = {
  xeniaCloudFOS: 'FO',
  iServeCloud: 'POS',
  onlineBookingChannel: 'OBC',
  hermesCloudBookkeeping: 'ABK',
  hermesCloudBookkeepingCAS: 'ABKCAS',
  iris: 'IRIS',
  ixorp: 'IXORP',
  website: 'WEB',
};

type SelectedProductRequirementKey = 'numberOfRooms' | 'numberOfOutlets';

export const PRODUCT_REQUIREMENTS: Partial<
  Record<
    ISubscriptionProduct,
    {
      label: string;
      name: SelectedProductRequirementKey;
      helperText?: string;
    }
  >
> = {
  'Xenia Cloud Front Office System': {
    label: '# of Room(s)',
    name: 'numberOfRooms',
  },
  'iServe Cloud Point of Sale': {
    label: '# of Outlet(s)',
    name: 'numberOfOutlets',
  },
};

export interface SelectedProduct {
  productName: ISubscriptionProduct;
  productCode: ISubscriptionProductCode;
  numberOfRooms?: number;
  numberOfOutlets?: number;
}

export type ISubscriptionInitialSelected = {
  xeniaCloudFOS?: boolean;

  iServeCloud?: boolean;
  iris?: boolean;
  ixorp?: boolean;
  website?: boolean;

  onlineBookingChannel?: boolean;
  hermesCloudBookkeeping?: boolean;
  hermesCloudBookkeepingCAS?: boolean;
};

// Complete Payment Portal Interface
export interface ISubscription {
  id?: string;
  url?: string;
  sameAsAuthorized?: boolean; // Additional property
  numberOfLicenses?: number; // Additional property
  propertyDetails: ISubscriptionPropertyDetails;
  authorizedPerson: ISubscriptionAuthorizedPerson;
  contactPersonBilling: ISubscriptionContactPersonBilling;
  // license: ISubscriptionLicense;
  selectedProducts: SelectedProduct[];
  initialSelected?: ISubscriptionInitialSelected;
  plan: ISubscriptionPlan;
}

interface ISubscriptionIndividualDetails {
  given_names: string | null;
  given_names_non_roman: string | null;
  surname: string | null;
  surname_non_roman: string | null;
  nationality: string | null;
  date_of_birth: string | null;
  place_of_birth: string | null;
  gender: string | null;
  employment: string | null;
}

// Interface for the Main Object
export interface ISubscriptionCustomerData {
  type: string;
  date_of_registration: string | null;
  email: string;
  mobile_number: string;
  phone_number: string | null;
  created: string;
  updated: string;
  description: string | null;
  hashed_phone_number: string | null;
  domicile_of_registration: string | null;
  kyc_documents: any[]; // assuming kyc_documents is an array of objects, replace 'any' with the correct type if known
  id: string;
  reference_id: string;
  metadata: any; // replace 'any' with the correct type if known
  individual_detail: ISubscriptionIndividualDetails;
  business_detail: any; // replace 'any' with the correct type if known
  addresses: any[]; // replace 'any' with the correct type if known
  identity_accounts: any[]; // replace 'any' with the correct type if known
}

export interface IXenditInvoiceResponse {
  id: string; // The unique identifier for the invoice
  external_id: string; // Your external reference ID
  user_id: string; // Xendit account ID
  status: 'PENDING' | 'PAID' | 'EXPIRED'; // The status of the invoice
  amount: number; // The total amount for the invoice
  payer_email: string; // The email of the payer
  description: string; // The description of the invoice
  invoice_url: string; // The URL where the user can pay the invoice
  expiry_date: string; // ISO 8601 timestamp when the invoice will expire
  created: string; // ISO 8601 timestamp of when the invoice was created
  updated: string; // ISO 8601 timestamp of the last update on the invoice
  paid_amount: number; // Amount paid so far, 0 if not paid yet
  payment_method: string | null; // Payment method used (e.g., CREDIT_CARD, BCA, etc.), null if unpaid
  payment_channel: string | null; // Payment channel used, null if unpaid
  customer: {
    email: string;
    given_names: string;
    surname: string;
    phone_number: string | null;
  } | null;
  currency: string; // Currency of the invoice (e.g., PHP)
  failure_redirect_url: string | null; // URL to redirect on failure
  success_redirect_url: string | null; // URL to redirect on success
}
